import { ImageIcon } from "@xola/icons";
import { Table, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import React from "react";
import { useHistory } from "react-router-dom";
import { SourceCopy, Transaction } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { getPaymentMethodName } from "../../../models/Payment";
import { payoutTypes } from "../../../models/Payout";
import { usePayoutFilterStore } from "../../../stores/reports/payoutFilterStore";
import { Currency } from "../../Common/Currency";
import { InfiniteList } from "../../Common/InfiniteList";
import { PaymentMethodIcon } from "../../Common/PaymentMethodIcon";
import { ProductImage } from "../../Common/ProductImage";
import { TableSkeleton } from "../../Common/Skeleton/TableSkeleton";
import { SourceIcon, SourceName } from "../../Common/SourceIcon";

export const PayoutTable = () => {
    const seller = useSeller();
    const history = useHistory();
    const dateParams = usePayoutFilterStore((state) => state.getDateParams());
    const params = { "type[in]": payoutTypes.join(","), ...dateParams };

    const {
        data: transactions,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteCollection<Transaction>("transactions", params);

    if (isLoading) {
        return <TableSkeleton rows={5} columns={6} />;
    }

    return (
        <>
            <InfiniteList hasMore={hasNextPage} isLoading={isLoading || isFetchingNextPage} onLoadMore={fetchNextPage}>
                <Table>
                    <Table.Head>
                        <Table.Row>
                            <Table.Header>Item</Table.Header>
                            <Table.Header>Date</Table.Header>
                            <Table.Header>Amount</Table.Header>
                            <Table.Header>Balance</Table.Header>
                            <Table.Header>Source</Table.Header>
                            <Table.Header>Method</Table.Header>
                        </Table.Row>
                    </Table.Head>

                    <Table.Body isStriped>
                        {transactions.map((transaction) => {
                            const productItem = transaction.items[0];
                            return (
                                <Table.Row
                                    key={transaction.id}
                                    className="cursor-pointer hover:bg-gray-lighter"
                                    onClick={() => history.push(`/reports/payouts/${transaction.id}`)}
                                >
                                    <Table.Cell>
                                        {productItem.orderItem ? (
                                            <Tooltip content="Payment from Xola">
                                                <ProductImage product={productItem.orderItem?.product} size="small" />
                                            </Tooltip>
                                        ) : (
                                            <div
                                                className={clsx(
                                                    "ui-product-image mr-3 flex-shrink-0 rounded-lg border-[3px] border-transparent",
                                                )}
                                            >
                                                <Tooltip content="Payment from Xola">
                                                    <ImageIcon size="large" />
                                                </Tooltip>
                                            </div>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell className="no-translate">
                                        {formatDate(transaction.createdAt, DateFormat.DATE_SHORT)}
                                    </Table.Cell>

                                    <Table.Cell className={clsx(transaction.amount < 0 && "text-red")}>
                                        <Currency>{transaction.amount}</Currency>
                                    </Table.Cell>
                                    <Table.Cell className={clsx(transaction.balance < 0 && "text-red")}>
                                        <Currency>{transaction.balance}</Currency>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <SourceIcon source={transaction.source} size="small" className="top-0 mr-2" />
                                        {SourceCopy[transaction.source] ?? <SourceName source={transaction.source} />}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Tooltip content={transaction.method}>
                                            <PaymentMethodIcon
                                                size="small"
                                                method={transaction.method}
                                                className="top-0 mr-2"
                                            />
                                            {getPaymentMethodName(seller, transaction.method)}
                                        </Tooltip>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </InfiniteList>

            {transactions.length === 0 && !isLoading ? (
                <div className="my-2 text-center text-base text-gray-dark">
                    No Payout data found. Try choosing a different date range
                </div>
            ) : null}
        </>
    );
};
